import { useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { getDocumentName, graphql } from '@e-socna/data-layer';
import { isDefined } from '@e-socna/utils';

import {
  PATH_USERS_DEFAULT,
  PATH_USER_DEFAULT,
  PATH_USER_USER_DEFAULT,
  PATH_USER_USER_ACCOUNT,
  PATH_USER_USER_AUTHORIZATIONS,
  PATH_USER_USER_AUTHORIZATIONS_EDIT,
  PATH_USER_USER_ADMIN,
  PATH_USER_USER_ADMIN_EDIT,
  PATH_USER_USER_SETTINGS,
  PATH_USER_PERSON_GENERAL,
  PATH_USER_PERSON_GENERAL_EDIT,
  PATH_USER_PERSON_CONTACT,
  PATH_USER_PERSON_CONTACT_EDIT,
  PATH_USER_PERSON_ADDRESSES,
  PATH_USER_PERSON_ADDRESSES_EDIT,
  PATH_USER_PERSON_ADDRESSES_EDIT_NEW,
  PATH_USER_PERSON_ADDRESSES_EDIT_MODIFY,
  PATH_USER_PERSON_ADDRESSES_EDIT_DELETE,
  PATH_USER_PERSON_BILLING,
  PATH_USER_PERSON_BILLING_EDIT,
  PATH_USER_PERSON_BANK,
  PATH_USER_PERSON_BANK_EDIT,
  PATH_USER_PERSON_MEMBERSHIPS,
  PATH_USER_PERSON_MEMBERSHIPS_EDIT,
  PATH_USER_PERSON_ADMIN,
  PATH_USER_PERSON_ADMIN_EDIT,
  PATH_USER_PERSON_VERSIONS,
  PATH_USER_PERSON_PARTICIPATIONS,
  PATH_USER_PERSON_PENNYLANE,
  PATH_USER_PERSON_DEFAULT,
} from '../../app/constants/paths.constant';
import { Tabs, TabsStrategy, TabsVariant } from '../../components/Tabs';
import { NoData } from '../../components/Illustrations/NoData';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { Loading } from '../../components/Loading';
import { useGraphQLQuery } from '../../hooks/useGraphQLQuery.hook';
import { useIdParam } from '../../utils/use-id-param.util';
import { MyDataAction } from '../../forms/MyData';
import { RegeneratePasswordAction } from '../../forms/RegeneratePassword';
import { useAuthenticationContext } from '../../contexts/AuthenticationContext/hooks/useAuthenticationContext.hook';
import { Page } from '../../contexts/NavigationContext/enums/page.enum';
import { useNavigationContext } from '../../contexts/NavigationContext/hooks/useNavigationContext.hook';
import { useBuildPath } from '../../hooks/useBuildPath.hook';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';

import { PersonTabs } from '../Person';
import { buildDocumentTitle } from '../../utils/document-title.util';
import { PageTitle } from '../../components/PageTitle';

import { User } from './User';
import type { UserPaths } from './User';

export { User as UserTabs };
export type { UserPaths };

export const UserPage_OneUser_QueryDocument = graphql(`
  query UserPage_OneUser_Query($id: Id!) {
    findOneUser(id: $id) {
      _id
      person {
        code
        fullName
        ...PersonPage_Tabs_Person_Fragment
      }
      ...UserPage_Tab_User_Fragment
    }
  }
`);

export const UserPage = () => {
  const { setPage } = useNavigationContext();
  const id = useIdParam();
  const { addressId } = useParams();

  const { data, isSuccess } = useGraphQLQuery(
    UserPage_OneUser_QueryDocument,
    { id },
    { enabled: !!id },
  );

  const user = data?.findOneUser;
  const person = user?.person;

  const { authenticationData } = useAuthenticationContext();
  const queryClient = useQueryClient();

  const refetchUser = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: [getDocumentName(UserPage_OneUser_QueryDocument)],
    });
  }, [queryClient]);

  // On their own account, users change their data like in « Mon Compte ».
  const accountActions =
    authenticationData?._id === id ? (
      <MyDataAction onUpdated={refetchUser} />
    ) : (
      <RegeneratePasswordAction userId={id} />
    );

  const pathParams = {
    id,
    addressId,
  };

  const defaultPath = useBuildPath(PATH_USER_DEFAULT, pathParams);

  const { paths: userPaths, currentPathKey } = useBuildPaths(
    {
      account: PATH_USER_USER_ACCOUNT,
      default: PATH_USER_USER_DEFAULT,
      authorizations: PATH_USER_USER_AUTHORIZATIONS,
      authorizationsEdit: PATH_USER_USER_AUTHORIZATIONS_EDIT,
      settings: PATH_USER_USER_SETTINGS,
      admin: PATH_USER_USER_ADMIN,
      adminEdit: PATH_USER_USER_ADMIN_EDIT,
    },
    pathParams,
  );

  const activeTab = isDefined(currentPathKey) ? 'user' : 'person';

  useEffect(() => {
    setPage(Page.Users);
  }, [setPage]);

  const beforeTitleContent = (
    <>
      <Helmet>
        <title>{buildDocumentTitle('Fiche Utilisateur', person?.code)}</title>
      </Helmet>
      <Breadcrumbs
        paths={[
          {
            to: PATH_USERS_DEFAULT,
            label: 'utilisateurs',
            key: 'users',
          },
          {
            to: defaultPath,
            label: 'fiche',
            key: 'user',
          },
        ]}
      />
    </>
  );

  const titleContent = <PageTitle title="Fiche Utilisateur" />;

  const { paths: personPaths } = useBuildPaths(
    {
      default: PATH_USER_PERSON_DEFAULT,
      generalInfo: PATH_USER_PERSON_GENERAL,
      generalInfoEdit: PATH_USER_PERSON_GENERAL_EDIT,
      contactInfo: PATH_USER_PERSON_CONTACT,
      contactInfoEdit: PATH_USER_PERSON_CONTACT_EDIT,
      addresses: PATH_USER_PERSON_ADDRESSES,
      addressesEdit: PATH_USER_PERSON_ADDRESSES_EDIT,
      addressesEditNew: PATH_USER_PERSON_ADDRESSES_EDIT_NEW,
      addressesEditModify: PATH_USER_PERSON_ADDRESSES_EDIT_MODIFY,
      addressesEditDelete: PATH_USER_PERSON_ADDRESSES_EDIT_DELETE,
      billing: PATH_USER_PERSON_BILLING,
      billingEdit: PATH_USER_PERSON_BILLING_EDIT,
      bankInfo: PATH_USER_PERSON_BANK,
      bankInfoEdit: PATH_USER_PERSON_BANK_EDIT,
      memberships: PATH_USER_PERSON_MEMBERSHIPS,
      membershipsEdit: PATH_USER_PERSON_MEMBERSHIPS_EDIT,
      participations: PATH_USER_PERSON_PARTICIPATIONS,
      pennylane: PATH_USER_PERSON_PENNYLANE,
      admin: PATH_USER_PERSON_ADMIN,
      adminEdit: PATH_USER_PERSON_ADMIN_EDIT,
      versions: PATH_USER_PERSON_VERSIONS,
    },
    pathParams,
  );

  if (isSuccess) {
    if (!person) {
      return (
        <>
          {beforeTitleContent}
          {titleContent}
          <NoData />
        </>
      );
    }

    return (
      <>
        {beforeTitleContent}
        <PageTitle code={person.code} title={`Compte ${person.fullName}`} />
        <Tabs
          activeTab={activeTab}
          id="user-account-tabs"
          strategy={TabsStrategy.Controlled}
          tabs={[
            {
              key: 'user',
              label: 'Utilisateur',
              panel: (
                <User
                  accountActions={accountActions}
                  paths={userPaths}
                  user={user}
                />
              ),
              value: 'user',
              to: userPaths.default,
            },
            {
              key: 'person',
              label: 'Personne Liée',
              panel: (
                <PersonTabs
                  paths={personPaths}
                  person={person}
                  variant={TabsVariant.Vertical}
                />
              ),
              value: 'person',
              to: personPaths.default,
            },
          ]}
          title="Onglets de l'écran de Fiche d'un Utilisateur"
        />
      </>
    );
  }

  return (
    <>
      <Loading />
      {beforeTitleContent}
      {titleContent}
    </>
  );
};
